import React, { useState, useCallback } from 'react';
import { useTranslation } from '@/i18n';
import { motion } from 'framer-motion';
import {
  X, CheckCircle, AlertTriangle, Shield, ExternalLink, Search, Edit3,
  Download, ChevronDown, ChevronUp, Play, VolumeX, Volume2,
  Bookmark, Flag, MessageCircle, Bell
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// FULL SITE REPORT COMPONENT — Unified single-card design (Phase 2)
// ═══════════════════════════════════════════════════════════════════════════

// Risk categories for DANGER sites
const DANGER_RISK_CATEGORIES = {
  IDENTITY_THEFT: {
    label: 'Identity Theft Risk',
    description: 'This site may collect personal or financial information without legitimate purpose. Do not enter your name, address, or ID documents.',
    keywords: ['personal', 'identity', 'data', 'information', 'steal']
  },
  PAYMENT_FRAUD: {
    label: 'Payment Fraud Risk',
    description: 'Transactions on this site may result in unauthorized charges, card cloning, or non-delivery of goods.',
    keywords: ['card', 'payment', 'charge', 'money', 'credit', 'debit', 'bank']
  },
  PHISHING: {
    label: 'Phishing Risk',
    description: 'This site may be impersonating a legitimate brand to steal your login credentials or personal data.',
    keywords: ['phishing', 'impersonat', 'fake', 'login', 'credential', 'password']
  },
  MALWARE: {
    label: 'Malware Risk',
    description: 'Visiting or downloading from this site may install harmful software on your device.',
    keywords: ['malware', 'virus', 'download', 'install', 'software', 'trojan']
  },
  NO_RECOURSE: {
    label: 'No Recourse Risk',
    description: 'If something goes wrong, you are unlikely to get a refund or receive any support.',
    keywords: ['refund', 'support', 'contact', 'response', 'help']
  },
  FAKE_PRODUCT: {
    label: 'Fake Product Risk',
    description: 'Products sold here may be counterfeit, unsafe, or never delivered.',
    keywords: ['fake', 'counterfeit', 'replica', 'knockoff', 'not delivered', 'never arrived']
  }
};

// Verdict styling
const VERDICT_CONFIG = {
  SAFE: {
    color: '#2DA563',
    bg: 'rgba(45, 165, 99, 0.10)',
    border: 'rgba(45, 165, 99, 0.3)',
    label: 'Looks Safe',
    scoreRange: [0, 30]
  },
  SAFE_WITH_CAUTION: {
    color: '#D4A017',
    bg: 'rgba(212, 160, 23, 0.10)',
    border: 'rgba(212, 160, 23, 0.3)',
    label: 'Safe with Caution',
    scoreRange: [31, 45]
  },
  CAUTION: {
    color: '#f97316',
    bg: 'rgba(249, 115, 22, 0.10)',
    border: 'rgba(249, 115, 22, 0.3)',
    label: 'Use Caution',
    scoreRange: [51, 70]
  },
  DANGER: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.10)',
    border: 'rgba(239, 68, 68, 0.3)',
    label: 'High Risk \u2014 Do Not Proceed',
    scoreRange: [71, 100]
  }
};

// Helper to derive score from verdict if not provided
const deriveScore = (verdict, providedScore) => {
  if (typeof providedScore === 'number' && providedScore >= 1 && providedScore <= 100) {
    return providedScore;
  }
  switch (verdict) {
    case 'SAFE': return 20;
    case 'SAFE_WITH_CAUTION': return 38;
    case 'CAUTION': return 55;
    case 'DANGER': return 80;
    default: return 50;
  }
};

// Safety state configuration (Tier 1 — no numeric score)
const SAFETY_STATE_CONFIG = {
  safe:             { color: '#2DA563', bg: 'rgba(45, 165, 99, 0.10)', label: 'Safe', style: 'solid' },
  looks_safe:       { color: '#2DA563', bg: 'rgba(45, 165, 99, 0.10)', label: 'Looks Safe', style: 'outline' },
  cautious:         { color: '#EAB308', bg: 'rgba(234, 179, 8, 0.10)', label: 'Be Cautious', style: 'solid' },
  unknown:          { color: '#8BA3C7', bg: 'rgba(139, 163, 199, 0.06)', label: 'Unknown Site', style: 'none' },
  be_cautious:      { color: '#EAB308', bg: 'rgba(234, 179, 8, 0.10)', label: 'Be Careful', style: 'outline' },
  looks_dangerous:  { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.10)', label: 'Looks Dangerous', style: 'outline' },
  dangerous:        { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.10)', label: 'Dangerous', style: 'solid' },
};

// Helper to detect relevant danger categories from bullets
const detectDangerCategories = (bullets = [], scamSignalLevel) => {
  const text = bullets.join(' ').toLowerCase();
  const categories = [];

  Object.entries(DANGER_RISK_CATEGORIES).forEach(([key, cat]) => {
    if (cat.keywords.some(kw => text.includes(kw))) {
      categories.push({ key, ...cat });
    }
  });

  if (categories.length === 0) {
    categories.push({ key: 'PAYMENT_FRAUD', ...DANGER_RISK_CATEGORIES.PAYMENT_FRAUD });
    if (scamSignalLevel === 'high') {
      categories.push({ key: 'NO_RECOURSE', ...DANGER_RISK_CATEGORIES.NO_RECOURSE });
    }
  }

  return categories.slice(0, 4);
};

// PDF Generation — preserved from original
const generatePDF = async (data) => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');

    const verdict = data.verdict || 'CAUTION';
    const config = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.CAUTION;
    const score = deriveScore(verdict, data.score);
    const displayScore = 100 - score;

    const LM = 20;
    const PW = 170;
    const PAGE_BOTTOM = 272;
    let y = 20;

    const VERDICT_RGB = {
      SAFE:               [45, 165, 99],
      SAFE_WITH_CAUTION:  [212, 160, 23],
      CAUTION:            [251, 146, 60],
      DANGER:             [239, 68, 68],
    };
    const vc = VERDICT_RGB[verdict] || VERDICT_RGB.CAUTION;

    const sanitize = (str) =>
      (str || '')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u2014\u2013]/g, '--')
        .replace(/[^\x20-\x7E\n]/g, '')
        .trim();

    const sectionHeader = (label) => {
      checkPage(12);
      doc.setDrawColor(200);
      doc.line(LM, y, LM + PW, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(label.toUpperCase(), LM, y);
      y += 6;
    };

    const bodyText = (text, indent = 0) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(sanitize(text), PW - indent);
      lines.forEach(line => {
        checkPage(5);
        doc.text(line, LM + indent, y);
        y += 5;
      });
    };

    const checkPage = (needed) => {
      if (y + needed > PAGE_BOTTOM) {
        doc.addPage();
        y = 20;
      }
    };

    // Header
    doc.setFillColor(13, 27, 42);
    doc.rect(0, 0, 210, 32, 'F');
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('HAVN Safety Report', LM, 15);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(192, 205, 224);
    doc.text('Independent website safety assessment  |  ' + new Date().toLocaleString(), LM, 24);
    y = 40;

    // Site Identity
    sectionHeader('SITE IDENTITY');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    const domainStr = sanitize(data.domain || 'Unknown');
    const badgeStr = data.site_category_badge ? '  [' + data.site_category_badge + ']' : '';
    doc.text(domainStr + badgeStr, LM, y);
    y += 7;

    const fullIdentity = data.ai_site_identity || data.site_identity || '';
    if (fullIdentity) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const idLines = doc.splitTextToSize(sanitize(fullIdentity), PW);
      idLines.forEach(l => { checkPage(5); doc.text(l, LM, y); y += 5; });
      y += 2;
    }

    if (data.years_established && data.years_established >= 2) {
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text('Established: ' + data.years_established + '+ years online', LM, y);
      y += 5;
    }
    y += 2;

    // Risk Score
    sectionHeader('SAFETY SCORE');
    const boxW = 40, boxH = 40;
    const boxX = LM + (PW - boxW) / 2;
    const boxY = y;
    checkPage(boxH + 25);
    doc.setDrawColor(...vc);
    doc.setLineWidth(0.7);
    doc.rect(boxX, boxY, boxW, boxH);
    doc.setLineWidth(0.2);
    doc.setFontSize(36);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...vc);
    const scoreStr = String(displayScore);
    const scoreW = doc.getTextWidth(scoreStr);
    doc.text(scoreStr, boxX + (boxW - scoreW) / 2, boxY + 27);
    y = boxY + boxH + 5;
    doc.setFontSize(14);
    const labelStr = sanitize(config.label);
    const labelW = doc.getTextWidth(labelStr);
    doc.text(labelStr, LM + (PW - labelW) / 2, y);
    y += 7;

    if (data.confidence_label) {
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);
      doc.setFont('helvetica', 'normal');
      const confStr = sanitize(data.confidence_label);
      const confW = doc.getTextWidth(confStr);
      doc.text(confStr, LM + (PW - confW) / 2, y);
      y += 5;
    }
    y += 3;

    // Danger block
    if (verdict === 'DANGER') {
      checkPage(30);
      doc.setFillColor(254, 242, 242);
      doc.rect(LM, y, PW, 25, 'F');
      doc.setTextColor(239, 68, 68);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('[!] HIGH RISK SITE', LM + 5, y + 8);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('We strongly recommend leaving this site immediately.', LM + 5, y + 15);
      doc.text('If you shared payment details, contact your bank now.', LM + 5, y + 20);
      y += 30;
    }

    // What we checked
    const pdfLooksGood = (data.ai_looks_good || data.looks_good || []).filter(f => f && f !== 'Nothing specific to flag');
    const pdfWatchOut = (data.ai_watch_out_for || data.watch_out_for || []).filter(f => f && f !== 'Nothing to flag');

    if (pdfLooksGood.length > 0 || pdfWatchOut.length > 0) {
      sectionHeader('WHAT WE CHECKED');
      const colW = PW / 2 - 5;
      const colStartY = y;

      doc.setFontSize(9); doc.setTextColor(45, 165, 99); doc.setFont('helvetica', 'bold');
      doc.text('LOOKS GOOD', LM, y); y += 5;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
      pdfLooksGood.forEach(f => {
        checkPage(5);
        const lines = doc.splitTextToSize('[OK] ' + sanitize(f), colW);
        lines.forEach(l => { doc.text(l, LM + 3, y); y += 4.5; });
      });
      if (pdfLooksGood.length === 0) {
        doc.setTextColor(150); doc.text('Nothing specific to flag', LM + 3, y); y += 5;
      }
      const leftEndY = y;

      y = colStartY;
      const rightX = LM + colW + 10;
      doc.setFontSize(9); doc.setTextColor(239, 68, 68); doc.setFont('helvetica', 'bold');
      doc.text('WATCH OUT FOR', rightX, y); y += 5;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
      pdfWatchOut.forEach(f => {
        checkPage(5);
        const lines = doc.splitTextToSize('[!] ' + sanitize(f), colW);
        lines.forEach(l => { doc.text(l, rightX + 3, y); y += 4.5; });
      });
      if (pdfWatchOut.length === 0) {
        doc.setTextColor(150); doc.text('Nothing to flag', rightX + 3, y); y += 5;
      }
      y = Math.max(leftEndY, y) + 4;
    }

    // Detailed analysis from AI
    const fr = data.ai_full_report || {};
    if (fr.site_overview || fr.customer_feedback_summary || fr.havn_recommendation) {
      sectionHeader('DETAILED ANALYSIS');
      if (fr.site_overview) {
        doc.setFontSize(9); doc.setTextColor(100); doc.setFont('helvetica', 'bold');
        doc.text('What this site does:', LM, y); y += 5;
        doc.setFont('helvetica', 'normal'); bodyText(fr.site_overview); y += 3;
      }
      if (fr.customer_feedback_summary) {
        checkPage(10);
        doc.setFontSize(9); doc.setTextColor(100); doc.setFont('helvetica', 'bold');
        doc.text('What customers say:', LM, y); y += 5;
        doc.setFont('helvetica', 'normal'); bodyText(fr.customer_feedback_summary); y += 3;
      }
      if (fr.trust_signals_detail?.length > 0) {
        checkPage(10);
        doc.setFontSize(9); doc.setTextColor(45, 165, 99); doc.setFont('helvetica', 'bold');
        doc.text('Positive signals:', LM, y); y += 5;
        doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40);
        fr.trust_signals_detail.forEach(s => {
          checkPage(10);
          doc.setFontSize(10); doc.text('[+] ' + sanitize(s.signal), LM + 3, y); y += 5;
          if (s.explanation) { bodyText(s.explanation, 6); }
        });
        y += 2;
      }
      if (fr.risk_signals_detail?.length > 0) {
        checkPage(10);
        doc.setFontSize(9); doc.setTextColor(239, 68, 68); doc.setFont('helvetica', 'bold');
        doc.text('Red flags found:', LM, y); y += 5;
        doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40);
        fr.risk_signals_detail.forEach(s => {
          checkPage(10);
          doc.setFontSize(10);
          doc.text('[!] ' + sanitize(s.signal) + (s.severity ? ' (' + s.severity + ')' : ''), LM + 3, y); y += 5;
          if (s.explanation) { bodyText(s.explanation, 6); }
        });
        y += 2;
      }
      if (fr.havn_recommendation) {
        checkPage(10);
        doc.setFontSize(9); doc.setTextColor(13, 27, 42); doc.setFont('helvetica', 'bold');
        doc.text('Our recommendation:', LM, y); y += 5;
        doc.setFont('helvetica', 'normal'); bodyText(fr.havn_recommendation); y += 3;
      }
    }

    // What people report
    const whatPeopleReport = data.ai_what_people_report || data.what_people_report;
    if (whatPeopleReport) {
      sectionHeader('WHAT PEOPLE REPORT');
      bodyText(whatPeopleReport); y += 2;
    }

    // Before you proceed checklist
    const pdfChecklist = data.ai_before_you_proceed || [];
    if (pdfChecklist.length > 0) {
      sectionHeader('BEFORE YOU PROCEED');
      doc.setFontSize(11); doc.setTextColor(40, 40, 40);
      pdfChecklist.forEach((item, i) => {
        checkPage(6);
        const lines = doc.splitTextToSize((i + 1) + '. ' + sanitize(item), PW - 3);
        lines.forEach(l => { doc.text(l, LM + 3, y); y += 5; });
        y += 1;
      });
      y += 2;
    }

    // Sources
    const pdfSources = fr.sources_consulted || [];
    if (pdfSources.length > 0 || data.research_results_count) {
      sectionHeader('SOURCES CONSULTED');
      doc.setFontSize(10); doc.setTextColor(80, 80, 80);
      pdfSources.forEach(s => {
        checkPage(5); doc.text('- ' + sanitize(s), LM + 3, y); y += 5;
      });
      if (data.research_results_count) {
        checkPage(5); doc.text(data.research_results_count + ' web sources analyzed', LM + 3, y); y += 5;
      }
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFillColor(13, 27, 42);
      doc.rect(0, 282, 210, 15, 'F');
      doc.setFontSize(8);
      doc.setTextColor(192, 205, 224);
      doc.text('Powered by HAVN  |  havn.com  |  Share this report with a family member for a second opinion', LM, 289);
      doc.text('Page ' + i + ' of ' + pageCount, LM + PW - 20, 289);
    }

    const filename = 'HAVN-Report-' + (data.domain || 'unknown') + '-' + new Date().toISOString().split('T')[0] + '.pdf';
    doc.save(filename);
    return true;
  } catch (error) {
    console.error('[FullSiteReport] PDF generation failed:', error);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT — Unified single card (no tabs)
// ═══════════════════════════════════════════════════════════════════════════

export const FullSiteReport = React.memo(({
  data,
  loading,
  error,
  onClose,
  onReplayVoice,
  onRetry,
  isPlaying,
  isMuted,
  onToggleMute,
  onSaveFavorite,
  onCommunityFlag,
  onAskCaregiver,
  onNotifyCaregiver,
  caregiverName,
  isAlreadySaved,
  isAlreadyFlagged,
  actionState = {},
  // Phase 2 props:
  onLookDeeper,
  onTypoGateConfirm,
  onTypoGateReject,
  // Phase 3 props:
  isDeepScanning,
  // Phase 3B props:
  onGenerateReport,
  isGeneratingReport,
}) => {
  const { t } = useTranslation();
  const [checkedItems, setCheckedItems] = useState({});
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    score: true,
    checked: true,
    danger: true,
    feedback: true,
    detailed: false,
  });

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <motion.div
        className="full-site-report"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        data-testid="full-site-report"
      >
        <div className="fsr-header">
          <span className="fsr-header-title">Checking...</span>
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
        <div className="fsr-loading">
          <div className="fsr-spinner" />
          <span>Analyzing this site...</span>
        </div>
      </motion.div>
    );
  }

  // ── Investigating state (Tier 2 pending) ─────────────────────────────────
  if (data?._investigating) {
    return (
      <motion.div
        className="full-site-report"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        data-testid="full-site-report"
      >
        <div className="fsr-header">
          <span className="fsr-header-title">Checking this site</span>
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
        <div style={{
          padding: '48px 24px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            border: '3px dashed var(--havn-primary, #3FAFD1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fsr-spin 3s linear infinite',
          }}>
            <span style={{ fontSize: 28 }}>🔍</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {data.domain || 'Unknown site'}
          </div>
          <div style={{ fontSize: 14, opacity: 0.7, lineHeight: 1.5, maxWidth: 280 }}>
            HAVN is taking a closer look — about 15 seconds
          </div>
          <div style={{
            marginTop: 8, height: 3, width: '60%', borderRadius: 2,
            background: 'rgba(255,255,255,0.1)', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: 'var(--havn-primary, #3FAFD1)',
              animation: 'havn-deep-progress 15s ease-in-out forwards',
              width: '5%',
            }} />
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Degraded state (Tier 2 failed) ───────────────────────────────────────
  if (data?._degraded || data?.tier2_failed) {
    return (
      <motion.div
        className="full-site-report"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        data-testid="full-site-report"
      >
        <div className="fsr-header">
          <span className="fsr-header-title">{data.domain || 'Unknown site'}</span>
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
        <div style={{
          padding: '48px 24px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            border: '3px dashed var(--havn-primary, #3FAFD1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 28 }}>⚠</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--havn-primary, #3FAFD1)' }}>
            Verification unavailable
          </div>
          <div style={{ fontSize: 14, opacity: 0.75, lineHeight: 1.5, maxWidth: 300 }}>
            HAVN could not fully check this site. Proceed with caution.
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                marginTop: 12, padding: '10px 20px',
                background: 'var(--havn-primary, #3FAFD1)',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Try again
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error && !data) {
    return (
      <motion.div
        className="full-site-report"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        data-testid="full-site-report"
      >
        <div className="fsr-header">
          <span className="fsr-header-title">Error</span>
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
        <div className="fsr-error">
          <AlertTriangle size={40} />
          <span>{error}</span>
          {onRetry && <button onClick={onRetry} className="fsr-retry-btn">Try Again</button>}
        </div>
      </motion.div>
    );
  }

  if (!data) return null;

  // ── Typo gate: dead domain ────────────────────────────────────────────────
  if (data?.typo_gate?.action === 'dead_domain') {
    return (
      <motion.div
        className="full-site-report"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <div className="fsr-header">
          <span className="fsr-header-title">{data.domain}</span>
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
        <div style={{
          padding: '32px 20px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
        }}>
          <AlertTriangle size={36} style={{ color: '#FBBF24' }} />
          <p style={{ fontSize: '15px', color: '#fff', fontWeight: 500 }}>
            {data.typo_gate.message}
          </p>
          <button
            onClick={onTypoGateReject}
            style={{
              padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
              background: 'rgba(63, 175, 209, 0.08)', border: '1px solid rgba(63, 175, 209, 0.25)',
              color: 'var(--havn-primary)', cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </motion.div>
    );
  }

  // ── Typo gate: typosquat danger ───────────────────────────────────────────
  if (data?.typo_gate?.action === 'typosquat_danger') {
    return (
      <motion.div
        className="full-site-report fsr-danger-mode"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <div className="fsr-header fsr-header-danger">
          <span className="fsr-header-title">{data.domain}</span>
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '10px', padding: '16px', marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <AlertTriangle size={18} style={{ color: '#ef4444' }} />
              <span style={{ color: '#ef4444', fontWeight: 600, fontSize: '14px' }}>
                This isn't {data.typo_gate.impersonated_brand}
              </span>
            </div>
            <p style={{ color: '#FECACA', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
              {data.typo_gate.message}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444', cursor: 'pointer',
              }}
            >
              Close this page
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const verdict = data.verdict || 'CAUTION';
  const config = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.CAUTION;
  const score = deriveScore(verdict, data.score);
  const displayScore = 100 - score; // Flip for user display: 100 = safest, 0 = most dangerous
  const isDanger = verdict === 'DANGER';
  const showChecklist = verdict === 'SAFE_WITH_CAUTION' || verdict === 'CAUTION';

  // Tier state
  const isTier2Loaded = !!data?.tier2_loaded;
  const confidence = data?.confidence || 'MEDIUM';
  if (data?.tier2_loaded !== undefined) {
    console.log('[FSR] tier2_loaded=', data.tier2_loaded, 'score=', data.score, 'verdict=', data.verdict, 'safety_state=', data.safety_state);
  }

  // Safety state (Tier 1)
  const safetyState = data?.safety_state || 'be_cautious';
  const stateConfig = SAFETY_STATE_CONFIG[safetyState] || SAFETY_STATE_CONFIG.be_cautious;

  // Site identity
  const siteIdentityText = data.ai_site_identity || data.site_identity || data.one_liner || null;
  const categoryBadge = data.site_category_badge || null;

  // Source count
  const sourceCount = data.research_results_count || 5;

  // Green flags
  const greenFlags = (data.ai_looks_good || data.looks_good || [])
    .filter(f => f && f !== 'Nothing specific to flag');

  // Red flags
  const redFlags = (data.ai_watch_out_for || data.watch_out_for || [])
    .filter(f => f && f !== 'Nothing to flag')
    .map((label, i) => ({ theme: `flag_${i}`, label }));

  // Danger risk categories
  const bullets = data.bullets || data.evidenceBullets || [];
  const dangerCategories = isDanger ? detectDangerCategories(bullets, data.scam_signal_level) : [];

  // Checklist items
  const checklistItems = (data.ai_before_you_proceed?.length)
    ? data.ai_before_you_proceed.map((q, i) => ({ id: `ai_${i}`, label: q }))
    : [
        { id: 'cancel', label: 'Can you find a clear cancellation option?' },
        { id: 'terms', label: 'Have you read the subscription terms?' },
        { id: 'returns', label: 'Did you check the returns policy?' },
      ];
  const hideChecklist = categoryBadge === 'NEWS' || categoryBadge === 'GOVERNMENT';

  // Confidence pill class
  const confPillClass = confidence === 'HIGH' ? 'conf-high' :
    confidence === 'MEDIUM' ? 'conf-medium' : 'conf-low';

  // Confirm gate — hide details until user confirms the address
  const typoAction = data?.typo_gate?.action;
  const isConfirmGate = !data?.tier2_loaded && (typoAction === 'confirm_address' || typoAction === 'probably_typo' || typoAction === 'unknown_site');

  // High-certainty DANGER — simplified card, no score ring, no Look Deeper
  const isHighCertaintyDanger = isDanger && data?.danger_certainty === 'high';

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCheckbox = (id) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    const success = await generatePDF(data);
    setIsGeneratingPDF(false);
    if (!success) {
      alert('PDF download requires an internet connection');
    }
  };

  return (
    <motion.div
      className={`full-site-report ${isDanger ? 'fsr-danger-mode' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      data-testid="full-site-report"
    >
      {/* ═══ HEADER ═══════════════════════════════════════════════════════════ */}
      <div className={`fsr-header ${isDanger ? 'fsr-header-danger' : ''}`}>
        <span className="fsr-header-title">Safety report</span>
        <div className="fsr-header-right">
          {data.ai_full_report && (
            <button className="fsr-download-btn" onClick={handleDownloadPDF} disabled={isGeneratingPDF}>
              <Download size={16} />
              <span>{isGeneratingPDF ? 'Generating...' : 'Download PDF'}</span>
            </button>
          )}
          <button className="fsr-close" onClick={onClose}><X size={22} /></button>
        </div>
      </div>

      {/* Banner + progress bar (hidden when confirm gate card handles the messaging) */}
      {data?.communication?.banner && !isConfirmGate && (
        <div style={{
          padding: '10px 16px', margin: '8px 12px 0', borderRadius: '8px',
          display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px',
          background: data.communication.banner.type === 'danger'
            ? 'rgba(239, 68, 68, 0.06)' : 'rgba(249, 115, 22, 0.06)',
          color: data.communication.banner.type === 'danger'
            ? '#F87171' : '#FBBF24',
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: data.communication.banner.type === 'danger' ? '#ef4444' : '#f97316',
          }} />
          <span>{data.communication.banner.text}</span>
        </div>
      )}
      {/* Progress bar — shows during deep scan even without a text banner */}
      {!data?.tier2_loaded && data?.auto_deep_scan && (
        <div style={{
          height: 3, margin: '6px 12px 0', borderRadius: 2,
          background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: 2, background: 'var(--havn-primary)',
            animation: 'deepScanProgress 15s ease-in-out forwards',
          }} />
        </div>
      )}

      <div className="fsr-content">
        {/* ═══ SECTION 1: SAFETY STATE (Tier 1) or RISK SCORE (Tier 2) ═══════
            Tier 1 owns the verdict band; Tier 2 owns the number. The
            numeric badge below renders ONLY when isTier2Loaded — fades
            in once deeper analysis arrives. */}
        <section className="fsr-section fsr-score">
          <h3 className="fsr-section-header" onClick={() => toggleSection('score')}>
            {isTier2Loaded ? t('section_risk_score') : t('section_safety_check')}
            {expandedSections.score ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </h3>
          {expandedSections.score && (
            <div className="fsr-section-content fsr-score-content">
              {isTier2Loaded ? (
                <div style={{ animation: 'fsrScoreFadeIn 360ms ease-out' }}>
                  <div className="fsr-score-badge" style={{ borderColor: config.color, background: config.bg }}>
                    <span className="fsr-score-number" style={{ color: config.color }}>{displayScore}</span>
                  </div>
                  <div className="fsr-verdict-label" style={{ color: config.color }}>{config.label}</div>
                  <div className="fsr-score-meta">Based on {sourceCount} sources</div>
                </div>
              ) : safetyState === 'unknown' ? (
                /* Unknown state: neutral gray, no color judgment, progress */
                <>
                  <div style={{
                    width: 72, height: 72, borderRadius: '50%',
                    border: '3px solid rgba(139, 163, 199, 0.3)',
                    background: 'rgba(139, 163, 199, 0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 10px',
                  }}>
                    <Search size={28} style={{ color: '#8BA3C7' }} />
                  </div>
                  <div className="fsr-verdict-label" style={{ color: '#8BA3C7', fontSize: '18px', fontWeight: 600 }}>
                    Unknown Site
                  </div>
                  <p style={{ color: '#6B829E', fontSize: '12px', margin: '4px 0 0', textAlign: 'center', maxWidth: 240 }}>
                    We haven't verified this site yet. We're checking it for you now.
                  </p>
                  {data?.auto_deep_scan && (
                    <div style={{ height: 3, margin: '10px auto 0', width: '80%', borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 2, background: 'var(--havn-primary)', animation: 'deepScanProgress 15s ease-in-out forwards' }} />
                    </div>
                  )}
                </>
              ) : (
                /* Known states: colored circle with icon */
                <>
                  <div style={{
                    width: 72, height: 72, borderRadius: '50%',
                    border: `4px ${stateConfig.style === 'solid' ? 'solid' : 'dashed'} ${stateConfig.color}`,
                    background: stateConfig.style === 'solid' ? stateConfig.bg : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 10px',
                  }}>
                    {stateConfig.color === '#2DA563' ? <CheckCircle size={32} style={{ color: stateConfig.color, opacity: stateConfig.style === 'solid' ? 1 : 0.7 }} /> :
                     stateConfig.color === '#ef4444' ? <AlertTriangle size={32} style={{ color: stateConfig.color, opacity: stateConfig.style === 'solid' ? 1 : 0.7 }} /> :
                     <Shield size={32} style={{ color: stateConfig.color, opacity: stateConfig.style === 'solid' ? 1 : 0.7 }} />}
                  </div>
                  <div className="fsr-verdict-label" style={{ color: stateConfig.color, fontSize: '18px', fontWeight: 600 }}>
                    {stateConfig.label}
                  </div>
                </>
              )}
              <span className={`fsr-confidence-pill ${confPillClass}`}>
                {data.confidence_label || 'Medium confidence'}
              </span>
            </div>
          )}
        </section>

        {/* ═══ TYPO GATE: PROBABLY TYPO ═══════════════════════════════════════ */}
        {typoAction === 'probably_typo' && (
          <div style={{
            padding: '28px 20px', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(251, 191, 36, 0.08)',
              border: '1.5px solid rgba(251, 191, 36, 0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Edit3 size={22} style={{ color: '#FBBF24' }} />
            </div>
            <h2 style={{ fontSize: '15px', fontWeight: 500, color: '#FFFFFF', margin: 0 }}>
              That doesn't look quite right
            </h2>
            <p style={{ fontSize: '13px', color: '#8BA3C7', lineHeight: 1.5, margin: 0, maxWidth: 280 }}>
              {data.typo_gate.message}
            </p>
            <div style={{ display: 'flex', gap: '8px', width: '100%', padding: '0 16px', marginTop: 4 }}>
              <button onClick={onTypoGateReject} style={{
                flex: 1, padding: '11px', fontSize: '13px', fontWeight: 500, borderRadius: '10px', cursor: 'pointer',
                background: 'rgba(63, 175, 209, 0.08)', border: '1px solid rgba(63, 175, 209, 0.25)', color: 'var(--havn-primary)',
              }}>
                Fix the address
              </button>
              <button onClick={onTypoGateConfirm} style={{
                flex: 1, padding: '11px', fontSize: '13px', fontWeight: 500, borderRadius: '10px', cursor: 'pointer',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#8BA3C7',
              }}>
                Continue anyway
              </button>
            </div>
          </div>
        )}

        {/* ═══ TYPO GATE: UNKNOWN SITE (friendly teal) ═════════════════════════ */}
        {(typoAction === 'unknown_site' || typoAction === 'confirm_address') && !data?.tier2_loaded && (
          <div style={{
            padding: '28px 20px', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(63, 175, 209, 0.08)',
              border: '1.5px solid rgba(63, 175, 209, 0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Search size={22} style={{ color: 'var(--havn-primary)' }} />
            </div>
            <p style={{ fontSize: '13px', color: '#8BA3C7', fontWeight: 500, margin: 0 }}>
              {data.domain}
            </p>
            <h2 style={{ fontSize: '15px', fontWeight: 500, color: '#FFFFFF', margin: 0 }}>
              We don't recognize this site yet
            </h2>
            <p style={{ fontSize: '13px', color: '#8BA3C7', lineHeight: 1.5, margin: 0, maxWidth: 280 }}>
              This isn't a site we've seen before. If you're sure about the address, we can run a thorough safety check for you.
            </p>
            <div style={{ display: 'flex', gap: '8px', width: '100%', padding: '0 16px', marginTop: 4 }}>
              <button onClick={onTypoGateConfirm} style={{
                flex: 1, padding: '11px', fontSize: '13px', fontWeight: 500, borderRadius: '10px', cursor: 'pointer',
                background: 'rgba(63, 175, 209, 0.08)', border: '1px solid rgba(63, 175, 209, 0.25)', color: 'var(--havn-primary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}>
                <Search size={16} />
                Check this site for me
              </button>
              <button onClick={onClose} style={{
                flex: 1, padding: '11px', fontSize: '13px', fontWeight: 500, borderRadius: '10px', cursor: 'pointer',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#8BA3C7',
              }}>
                Not right now
              </button>
            </div>
          </div>
        )}

        {/* ═══ EVERYTHING BELOW IS HIDDEN DURING CONFIRM GATE ═════════════════ */}
        {!isConfirmGate && isHighCertaintyDanger && (
          /* High-certainty DANGER — simplified red card, no score, no flags */
          <div style={{ padding: '12px' }}>
            <div style={{
              background: 'rgba(239, 68, 68, 0.06)',
              border: '0.5px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '10px', padding: '16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <AlertTriangle size={18} style={{ color: '#ef4444' }} />
                <span style={{ color: '#ef4444', fontWeight: 500, fontSize: '14px' }}>
                  {data.typo_gate?.impersonated_brand
                    ? `This isn't ${data.typo_gate.impersonated_brand}`
                    : 'This site has been flagged as harmful'}
                </span>
              </div>
              <p style={{ color: '#FECACA', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
                {data.typo_gate?.message ||
                  'This site has been identified by security databases as potentially harmful. We recommend closing this page.'}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '100%', marginTop: '10px', padding: '10px',
                borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                background: 'rgba(239, 68, 68, 0.08)',
                border: '0.5px solid rgba(239, 68, 68, 0.25)',
                color: '#ef4444', cursor: 'pointer',
              }}
            >
              Close this page
            </button>
          </div>
        )}
        {!isConfirmGate && !isHighCertaintyDanger && (
          <>
            {/* ═══ SITE IDENTITY ═══════════════════════════════════════════════ */}
            <section className="fsr-section fsr-identity">
              <h3 className="fsr-section-header" onClick={() => toggleSection('identity')}>
                {t('section_site_identity')}
                {expandedSections.identity ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </h3>
              {expandedSections.identity && (
                <div className="fsr-section-content">
                  <div className="fsr-domain">
                    {data.domain}
                    {categoryBadge && (
                      <span className="fsr-meta-pill" style={{ marginLeft: 8, fontSize: '0.75em', fontWeight: 600 }}>
                        {categoryBadge}
                      </span>
                    )}
                  </div>
                  {data.brand_name && <div className="fsr-brand">{data.brand_name}</div>}
                  {siteIdentityText && (
                    <p className="fsr-description">{siteIdentityText}</p>
                  )}
                  <div className="fsr-meta-row">
                    {data.years_established > 0 && (
                      <span className="fsr-meta-pill">{data.years_established}+ years</span>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* ═══ WHAT THIS SITE DOES ═════════════════════════════════════════ */}
            {data.ai_what_this_site_does && (
              <section className="fsr-section">
                <h3 className="fsr-section-header" style={{ color: 'var(--havn-primary)' }}>
                  {t('section_what_site_does')}
                </h3>
                <div className="fsr-section-content">
                  <p className="fsr-description" style={{ fontSize: '14px', lineHeight: 1.6 }}>
                    {data.ai_what_this_site_does}
                  </p>
                </div>
              </section>
            )}

            {/* ═══ DANGER RISK BREAKDOWN (DANGER only) ═════════════════════════ */}
            {isDanger && (
              <section className="fsr-section fsr-danger-breakdown">
                <div className="fsr-danger-card">
                  <h3 className="fsr-danger-header">Why this site is High Risk</h3>
                  <div className="fsr-danger-categories">
                    {dangerCategories.map((cat, i) => (
                      <div key={i} className="fsr-danger-category">
                        <span className="fsr-danger-category-label">[{cat.label}]</span>
                        <p className="fsr-danger-category-desc">{cat.description}</p>
                      </div>
                    ))}
                  </div>
                  <div className="fsr-danger-banner">
                    <div className="fsr-danger-banner-text">
                      <strong>We strongly recommend leaving this site immediately.</strong>
                      <br />
                      If you have already shared payment details, contact your bank now.
                    </div>
                  </div>
                  <div className="fsr-danger-actions">
                    <button className="fsr-leave-btn" onClick={onClose}>
                      Leave this site
                    </button>
                    <button className="fsr-continue-btn" onClick={() => setExpandedSections(prev => ({ ...prev, danger: false }))}>
                      I understand, continue anyway
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* ═══ WHAT WE CHECKED ════════════════════════════════════════════ */}
            <section className="fsr-section fsr-checked">
              <h3 className="fsr-section-header" onClick={() => toggleSection('checked')}>
                {t('section_what_checked')}
                {expandedSections.checked ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </h3>
              {expandedSections.checked && (
                <div className="fsr-section-content fsr-checked-content">
                  <div className="fsr-checked-columns">
                    {/* Green column */}
                    <div className="fsr-checked-col fsr-checked-green">
                      <span className="fsr-checked-col-header">{t('looks_good')}</span>
                      {greenFlags.length > 0 ? (
                        greenFlags.map((flag, i) => (
                          <div key={i} className="fsr-checked-item fsr-item-green">
                            <CheckCircle size={14} />
                            <span>{flag}</span>
                          </div>
                        ))
                      ) : (
                        <div className="fsr-checked-empty">{t('nothing_flag')}</div>
                      )}
                    </div>
                    {/* Red column */}
                    <div className="fsr-checked-col fsr-checked-red">
                      <span className="fsr-checked-col-header">{t('watch_out')}</span>
                      {redFlags.length > 0 ? (
                        redFlags.map((flag, i) => (
                          <div key={i} className="fsr-checked-item fsr-item-red">
                            <AlertTriangle size={14} />
                            <span>{flag.label}</span>
                          </div>
                        ))
                      ) : (
                        <div className="fsr-checked-empty">{t('nothing_flag')}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ═══ BEFORE YOU PROCEED CHECKLIST (CAUTION only, hidden for news/gov) ══ */}
            {showChecklist && !hideChecklist && (
              <section className="fsr-section fsr-checklist">
                <h3 className="fsr-section-header">{t('section_before_proceed')}</h3>
                <div className="fsr-section-content">
                  {checklistItems.map(item => (
                    <label key={item.id} className="fsr-checklist-item">
                      <input
                        type="checkbox"
                        checked={checkedItems[item.id] || false}
                        onChange={() => handleCheckbox(item.id)}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* ═══ CUSTOMER FEEDBACK (Tier 1 basic) ═══════════════════════════ */}
            {(data.ai_what_people_report || (redFlags.length > 0 && verdict !== 'SAFE')) && (
              <section className="fsr-section fsr-feedback">
                <h3 className="fsr-section-header" onClick={() => toggleSection('feedback')}>
                  {t('section_people_report')}
                  {expandedSections.feedback ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </h3>
                {expandedSections.feedback && (
                  <div className="fsr-section-content">
                    {data.ai_what_people_report ? (
                      <p className="fsr-feedback-text">{data.ai_what_people_report}</p>
                    ) : (
                      <p className="fsr-feedback-empty">No significant complaints found</p>
                    )}
                    <p className="fsr-feedback-note">
                      Based on aggregated public reviews and consumer reports
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* ═══ TIER 2 SECTIONS (appear after deep scan) ═══════════════════ */}

            {/* Before you proceed checklist (Tier 2) */}
            {data?.ai_before_you_proceed?.length > 0 && (
              <section className="fsr-section fsr-checklist">
                <h3 className="fsr-section-header">Before you proceed</h3>
                <div className="fsr-section-content">
                  {data.ai_before_you_proceed.map((item, i) => (
                    <label key={i} className="fsr-checklist-item">
                      <input
                        type="checkbox"
                        checked={checkedItems[`deep_${i}`] || false}
                        onChange={() => handleCheckbox(`deep_${i}`)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Detailed report — lazy, on-demand */}
            {data?.tier2_loaded && (
              <div style={{ padding: '0 12px 10px' }}>
                {data?.ai_full_report ? (
                  <>
                    {/* Report content — already generated */}
                    <section className="fsr-section fsr-detailed-report">
                      <h3 className="fsr-section-header" onClick={() => toggleSection('detailed')}>
                        {t('section_detailed')}
                        {expandedSections.detailed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </h3>
                      {expandedSections.detailed && (
                        <div className="fsr-section-content" style={{ fontSize: '0.95em', lineHeight: 1.6 }}>
                          {data.ai_full_report.site_overview && (
                            <div style={{ marginBottom: 12 }}>
                              <p style={{ color: '#C0CDE0', fontSize: '13px' }}>{data.ai_full_report.site_overview}</p>
                            </div>
                          )}
                          {data.ai_full_report.trust_signals_detail?.map((s, i) => (
                            <div key={`t${i}`} style={{
                              padding: '8px 12px', marginBottom: '6px',
                              borderLeft: '2px solid #2DA563', borderRadius: 0,
                              background: 'rgba(45, 165, 99, 0.06)',
                            }}>
                              <strong style={{ color: '#4ADE80', fontSize: '12px' }}>{s.signal}</strong>
                              {s.explanation && <p style={{ margin: '2px 0 0', color: '#C0CDE0', fontSize: '12px' }}>{s.explanation}</p>}
                            </div>
                          ))}
                          {data.ai_full_report.risk_signals_detail?.map((s, i) => (
                            <div key={`r${i}`} style={{
                              padding: '8px 12px', marginBottom: '6px',
                              borderLeft: '2px solid #f97316', borderRadius: 0,
                              background: 'rgba(249, 115, 22, 0.06)',
                            }}>
                              <strong style={{ color: '#F87171', fontSize: '12px' }}>{s.signal} {s.severity ? `(${s.severity})` : ''}</strong>
                              {s.explanation && <p style={{ margin: '2px 0 0', color: '#C0CDE0', fontSize: '12px' }}>{s.explanation}</p>}
                            </div>
                          ))}
                          {data.ai_full_report.customer_feedback_summary && (
                            <div style={{ marginTop: 8 }}>
                              <p style={{ color: '#8BA3C7', fontSize: '12px', fontWeight: 500, marginBottom: 4 }}>What customers say</p>
                              <p style={{ color: '#C0CDE0', fontSize: '13px' }}>{data.ai_full_report.customer_feedback_summary}</p>
                            </div>
                          )}
                          {data.ai_full_report.havn_recommendation && (
                            <div style={{ marginTop: 8 }}>
                              <p style={{ color: '#8BA3C7', fontSize: '12px', fontWeight: 500, marginBottom: 4 }}>Our recommendation</p>
                              <p style={{ color: '#C0CDE0', fontSize: '13px' }}>{data.ai_full_report.havn_recommendation}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </section>

                    {/* PDF download — only after report is generated */}
                    <button
                      onClick={handleDownloadPDF}
                      disabled={isGeneratingPDF}
                      style={{
                        width: '100%', padding: '8px', fontSize: '11px', marginTop: '6px',
                        borderRadius: '8px', border: '0.5px solid rgba(255,255,255,0.1)',
                        background: 'transparent', color: '#8BA3C7', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                      }}
                    >
                      <Download size={13} />
                      {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF report'}
                    </button>
                  </>
                ) : (
                  /* Report not yet generated — show button to request it */
                  <button
                    onClick={onGenerateReport}
                    disabled={isGeneratingReport}
                    style={{
                      width: '100%', padding: '10px 16px', fontSize: '13px', fontWeight: 500,
                      background: isGeneratingReport ? 'var(--havn-primary-10)' : 'var(--havn-primary-10)',
                      border: '1px solid rgba(63, 175, 209, 0.25)',
                      borderRadius: '8px',
                      color: isGeneratingReport ? '#6B829E' : 'var(--havn-primary)',
                      cursor: isGeneratingReport ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    <Download size={15} />
                    {isGeneratingReport ? 'Generating detailed report...' : 'Download detailed report'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ VOICE CONTROLS (hidden during confirm gate and high-certainty DANGER) ═══ */}
      {!isConfirmGate && !isHighCertaintyDanger && (
        <div className="fsr-voice-controls">
          <button
            className={`fsr-voice-btn ${isMuted ? 'muted' : ''}`}
            onClick={onToggleMute}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            <span>{isMuted ? t('muted') : t('mute')}</span>
          </button>
          <button
            className={`fsr-voice-btn ${isPlaying ? 'playing' : ''}`}
            onClick={onReplayVoice}
            disabled={isPlaying}
          >
            <Play size={18} />
            <span>{isPlaying ? t('playing') : t('replay')}</span>
          </button>
        </div>
      )}

      {/* ═══ MEDIUM-CERTAINTY DANGER: Close + Look Deeper side by side ═════════ */}
      {!isConfirmGate && !isHighCertaintyDanger && isDanger && !data?.tier2_loaded && (
        <div style={{ display: 'flex', gap: '8px', padding: '0 12px 10px' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
            background: 'rgba(239, 68, 68, 0.08)', border: '0.5px solid rgba(239, 68, 68, 0.25)',
            color: '#ef4444', cursor: 'pointer',
          }}>
            Close this page
          </button>
          {onLookDeeper && (
            <button onClick={onLookDeeper} disabled={isDeepScanning} style={{
              flex: 1, padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
              background: 'rgba(63, 175, 209, 0.06)', border: '0.5px solid rgba(63, 175, 209, 0.2)',
              color: 'var(--havn-primary)', cursor: 'pointer',
            }}>
              {isDeepScanning ? t('report_checking') : t('look_deeper')}
            </button>
          )}
        </div>
      )}

      {/* ═══ LOOK DEEPER — visible on ALL states ══════════════════════════════ */}
      {!isConfirmGate && !isHighCertaintyDanger && onLookDeeper && !data?.tier2_loaded && (
        <div style={{ padding: '0 12px 10px' }}>
          <button
            onClick={onLookDeeper}
            disabled={isDeepScanning}
            style={{
              width: '100%', padding: '10px 16px', fontSize: '13px', fontWeight: 500,
              background: isDeepScanning ? 'var(--havn-primary-10)' : 'var(--havn-primary-10)',
              border: '1px solid var(--havn-primary-20)',
              borderRadius: '8px',
              color: isDeepScanning ? '#6B829E' : 'var(--havn-primary)',
              cursor: isDeepScanning ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            {isDeepScanning ? (
              <>Analyzing &mdash; about 15 seconds</>
            ) : (
              <><Search size={15} /> {data?.deep_scan_reason || 'Look deeper into this site'}</>
            )}
          </button>
        </div>
      )}

      {/* ═══ ACTION BUTTONS (hidden during confirm gate and high-certainty DANGER) ═══ */}
      {!isConfirmGate && !isHighCertaintyDanger && (
        <div className="fsr-actions">
          {onSaveFavorite && (verdict === 'SAFE' || verdict === 'SAFE_WITH_CAUTION' || isAlreadySaved || actionState.saved) && (
            <button
              className={`fsr-action-btn fsr-save ${isAlreadySaved || actionState.saved ? 'done' : ''}`}
              onClick={onSaveFavorite}
              disabled={actionState.saving}
            >
              <Bookmark size={15} fill={isAlreadySaved || actionState.saved ? 'currentColor' : 'none'} />
              <span>{actionState.saving ? (isAlreadySaved ? t('removing') : t('saving')) : isAlreadySaved || actionState.saved ? t('saved') : t('save')}</span>
            </button>
          )}
          {onCommunityFlag && ['CAUTION', 'DANGER'].includes(verdict) && (
            <button
              className={`fsr-action-btn fsr-flag ${isAlreadyFlagged || actionState.flagged ? 'done' : ''}`}
              onClick={onCommunityFlag}
              disabled={isAlreadyFlagged || actionState.flagged || actionState.flagging}
            >
              <Flag size={15} />
              <span>{actionState.flagging ? t('flagging') : isAlreadyFlagged || actionState.flagged ? t('flagged') : t('flag_site')}</span>
            </button>
          )}
          {onAskCaregiver && caregiverName && (
            <button
              className={`fsr-action-btn fsr-ask ${actionState.askedCaregiver ? 'done' : ''}`}
              onClick={onAskCaregiver}
              disabled={actionState.askingCaregiver || actionState.askedCaregiver}
            >
              <MessageCircle size={15} />
              <span>{actionState.askingCaregiver ? t('asking') : actionState.askedCaregiver ? t('asked') : t('ask_caregiver', { name: caregiverName })}</span>
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
});

FullSiteReport.displayName = 'FullSiteReport';

export default FullSiteReport;
